import { BreakableEmail } from "../../UI/Table/BreakableEmail.tsx";
import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Card } from "../../UI/Card.tsx";
import { BasicTable } from "../../UI/Table/BasicTable.tsx";
import { TablePagination } from "../../UI/Table/TablePagination.tsx";
import { useClientPagination } from "../../UI/Table/useClientPagination.ts";
import { StackedCell } from "./StackedCell.tsx";
import { SpeakingLevelWithGroup } from "../SpeakingLevelWithGroup.tsx";
import { SectionTitle } from "./SectionTitle.tsx";
import { InactiveStudent } from "./useInactiveStudentStatistics.ts";

const DATE_FORMAT = "D MMM YYYY";
const STUDENTS_PER_PAGE = 20;
const NO_STUDENTS: InactiveStudent[] = [];
const HIDDEN_ON_MOBILE = { className: "hidden md:table-cell" };

const columnHelper = createColumnHelper<InactiveStudent>();

function describePlans(student: InactiveStudent): string {
  if (student.hasActiveGroupPlan && student.hasActiveOneOnOnePlan)
    return "Group + 1to1";
  return student.hasActiveOneOnOnePlan ? "1to1" : "Group";
}

function daysAgo(date: string, today: Dayjs): string {
  const days = today.startOf("day").diff(dayjs(date).startOf("day"), "day");
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

export function InactiveStudentsTable({
  inactiveStudents,
  inactivityWindowDays,
  today,
  onSelectStudent,
}: {
  inactiveStudents: InactiveStudent[] | null;
  inactivityWindowDays: number | null;
  today: Dayjs;
  onSelectStudent: (userId: string) => void;
}) {
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "student",
        header: "Student",
        cell: ({ row }) => (
          <StackedCell
            primary={`${row.original.givenName} ${row.original.familyName}`}
            secondary={<BreakableEmail email={row.original.email} />}
          />
        ),
      }),
      columnHelper.accessor("speakingLevel", {
        header: "Level",
        meta: HIDDEN_ON_MOBILE,
        cell: ({ row }) => (
          <SpeakingLevelWithGroup
            speakingLevel={row.original.speakingLevel}
            userGroupName={row.original.userGroupName}
          />
        ),
      }),
      columnHelper.display({
        id: "plan",
        header: "Plan",
        meta: HIDDEN_ON_MOBILE,
        cell: ({ row }) => (
          <StackedCell
            primary={describePlans(row.original)}
            secondary={`until ${dayjs(row.original.lastPlanEndUtc).format(DATE_FORMAT)}`}
          />
        ),
      }),
      columnHelper.accessor("lastAttendedLessonEndUtc", {
        header: "Last lesson",
        cell: ({ getValue }) => {
          const lastLesson = getValue();
          return lastLesson ? (
            <StackedCell
              primary={daysAgo(lastLesson, today)}
              secondary={dayjs(lastLesson).format(DATE_FORMAT)}
            />
          ) : (
            <StackedCell primary={"Never attended"} />
          );
        },
      }),
      columnHelper.accessor("nextBookedLessonStartUtc", {
        header: "Next lesson",
        meta: HIDDEN_ON_MOBILE,
        cell: ({ getValue }) => {
          const nextLesson = getValue();
          return nextLesson ? (
            <StackedCell primary={dayjs(nextLesson).format(DATE_FORMAT)} />
          ) : (
            <span className={"text-slate-400"}>None booked</span>
          );
        },
      }),
    ],
    [today],
  );

  const { itemsOnCurrentPage, currentPage, totalPages, toPage } =
    useClientPagination(inactiveStudents ?? NO_STUDENTS, STUDENTS_PER_PAGE);

  const table = useReactTable({
    columns,
    data: itemsOnCurrentPage,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card>
      <div className={"flex flex-col gap-1"}>
        <SectionTitle>
          Inactive students
          {inactiveStudents ? ` · ${inactiveStudents.length}` : ""}
        </SectionTitle>
        {inactivityWindowDays !== null && (
          <p className={"text-xs text-slate-400"}>
            Students with an active group or 1to1 plan who have not attended any
            lesson in the last {inactivityWindowDays} days. Only students whose
            plan was already running {inactivityWindowDays} days ago are
            considered, students who froze a week in this period are left out,
            and lessons marked as no-show do not count as attended. The students
            inactive for the longest time come first.
          </p>
        )}
      </div>
      {inactiveStudents === null ? (
        <LoadingRows rows={8} />
      ) : inactiveStudents.length === 0 ? (
        <p className={"text-sm text-slate-400"}>No inactive students.</p>
      ) : (
        <div className={"flex flex-col gap-4"}>
          <BasicTable
            table={table}
            compact
            scrollableMinWidthClassName={"min-w-full"}
            onRowClick={(student) => onSelectStudent(student.userId)}
          />
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChangePage={toPage}
          />
        </div>
      )}
    </Card>
  );
}
